import { ExclamationCircleIcon } from "@heroicons/react/outline";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { type OfflineTaskUpload } from "~/config/offline-storage/offlineTaskUploads.store";
import { Box } from "~/design-system/Box";
import { Inline } from "~/design-system/Inline";
import { Text } from "~/design-system/Text";
import { ErrorUploadItem } from "./ErrorUploadItem";

interface ErrorUploadsSectionProps {
  errorUploads: OfflineTaskUpload[];
  expandedErrors: Set<string>;
  onToggleExpansion: (uploadId: string) => void;
  onRemove: (upload: OfflineTaskUpload) => void;
}

export const ErrorUploadsSection: React.FC<ErrorUploadsSectionProps> = ({
  errorUploads,
  expandedErrors,
  onRemove,
  onToggleExpansion,
}) => {
  const { t } = useTranslation();

  if (errorUploads.length === 0) {
    return null;
  }

  return (
    <Box>
      <Inline space="xsmall" alignY="center">
        <ExclamationCircleIcon
          height={16}
          width={16}
          style={{ color: "var(--colors-critical500)" }}
        />
        <Text size="small" tone="critical" weight="medium">
          {t("Errors")} ({errorUploads.length})
        </Text>
      </Inline>
      <Box paddingTop="xxsmall">
        {errorUploads.slice(0, 3).map((upload) => (
          <ErrorUploadItem
            key={upload.id}
            upload={upload}
            isExpanded={expandedErrors.has(upload.id)}
            onToggleExpansion={onToggleExpansion}
            onRemove={onRemove}
          />
        ))}
        {errorUploads.length > 3 && (
          <Text size="xsmall" tone="neutral">
            +{errorUploads.length - 3} more
          </Text>
        )}
      </Box>
    </Box>
  );
};
